import {
  QueryHookOptions,
  QueryResult,
  gql,
  useQuery,
} from '@nerdwallet/apollo';
import { MockedResponse } from '@nerdwallet/apollo/testing';

import { formatMoney } from '@lib/money';
import { RewardableEventType } from '@lib/types/nerdwallet-plus-rewards';
import {
  RewardableEventType as GeneratedRewardableEventType,
  FinancialBankingRewardEnum,
  FinancialCreditCardRewardEnum,
} from '@generated-gql/generated-types';

const POINTS_MONEY_PROPORTION = 1 / 100;

export const getRewardEventPointsLabel = (points: number): string => {
  return `${points} pts (${formatMoney(points * POINTS_MONEY_PROPORTION, true)})`;
};

export const GET_REWARD_EVENT_POINTS = gql`
  query GetRewardEventPointsUseRewardsPointsHook(
    $eventTypes: [String!]
    $includeAll: Boolean
  ) {
    rewardEventPoints(eventTypes: $eventTypes, includeAll: $includeAll) {
      rewards {
        eventType
        points
      }
    }
  }
`;

export interface RewardEventPoint {
  eventType:
    | RewardableEventType
    | FinancialBankingRewardEnum
    | FinancialCreditCardRewardEnum
    | GeneratedRewardableEventType;
  points: number | string;
}

type MappedData<T extends RewardableEventType> = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [key in T]?: number;
};

const useRewardsPoints = <T extends RewardableEventType>(
  eventTypes?: T[],
  includeAll?: boolean,
  options?: QueryHookOptions,
): QueryResult<MappedData<T>> => {
  const result = useQuery(GET_REWARD_EVENT_POINTS, {
    ...options,
    variables: {
      eventTypes,
      includeAll,
    },
    skip: (!eventTypes?.length && !includeAll) || options?.skip,
  });

  const transformPointsData = (data: RewardEventPoint[]): MappedData<T> => {
    return data?.reduce((acc, item) => {
      acc[item.eventType] = item.points;
      return acc;
    }, {});
  };

  return {
    ...result,
    data:
      transformPointsData(result.data?.rewardEventPoints?.rewards) || undefined,
  };
};

export const mockUseRewardsPointsResponse = ({
  eventTypes,
}: {
  eventTypes: RewardableEventType[];
}): MockedResponse => ({
  request: {
    query: GET_REWARD_EVENT_POINTS,
    variables: {
      eventTypes,
    },
  },
  result: MOCKED_REWARDS_POINTS,
});

const MOCKED_REWARDS_POINTS = {
  data: {
    rewardEventPoints: {
      aggregateRewards: [
        {
          eventType: 'CASH_FLOW',
          points: 400,
          reactors: null,
        },
      ],
      rewards: [
        {
          eventType: 'ACHIEVED_TWENTY_PERCENT_SAVINGS',
          points: 150,
          reactors: [
            {
              points: 150,
              reactorName: 'Achieved 20% savings',
            },
          ],
        },
        {
          eventType: 'ACQUIRED_IDENTITY_THEFT_PLAN',
          points: 5000,
          reactors: [
            {
              points: 2500,
              reactorName: 'Acquired Identity Theft Couple Plan',
            },
            {
              points: 5000,
              reactorName: 'Acquired Identity Theft Family Plan',
            },
            {
              points: 1000,
              reactorName: 'Acquired Identity Theft Individual Plan',
            },
          ],
        },
        {
          eventType: 'ACTIVATED_FINANCIAL_HEALTH_SCORE',
          points: 300,
          reactors: [
            {
              points: 300,
              reactorName: 'Activated Financial Health Score',
            },
          ],
        },
        {
          eventType: 'ACTIVATED_TU',
          points: 500,
          reactors: [
            {
              points: 500,
              reactorName: 'Activated TransUnion',
            },
          ],
        },
        {
          eventType: 'AUDIENCE_JOINED',
          points: 100,
          reactors: [
            {
              points: 100,
              reactorName: 'Active newly registered user (3 consecutive days)',
            },
          ],
        },
        {
          eventType: 'ADDED_FIRST_BILL',
          points: 100,
          reactors: [
            {
              points: 100,
              reactorName: 'Added first bill',
            },
            {
              points: 0,
              reactorName: 'My Test Reactor',
            },
          ],
        },
        {
          eventType: 'BOOSTED_CS_AVERAGE_GOOD',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Boosted Credit Score - Average to Good',
            },
          ],
        },
        {
          eventType: 'BOOSTED_CS_GOOD_EXCELLENT',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Boosted Credit Score - Good to Excellent',
            },
          ],
        },
        {
          eventType: 'BOOSTED_CS_POOR_AVERAGE',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Boosted Credit Score - Poor to Average',
            },
          ],
        },
        {
          eventType: 'CATEGORIZED_LAST_MONTH_TRANSACTIONS',
          points: 50,
          reactors: [
            {
              points: 50,
              reactorName: 'Categorized last month transactions',
            },
          ],
        },
        {
          eventType: 'COMPLETED_WEEKLY_INSIGHTS_STACK',
          points: 25,
          reactors: [
            {
              points: 25,
              reactorName: 'Completed Weekly Insights stack',
            },
          ],
        },
        {
          eventType: 'ENABLED_EMAIL_MARKETING_NOTIFICATIONS',
          points: 50,
          reactors: [
            {
              points: 50,
              reactorName: 'Enabled email notifications',
            },
          ],
        },
        {
          eventType: 'ENABLED_PUSH_NOTIFICATIONS',
          points: 50,
          reactors: [
            {
              points: 50,
              reactorName: 'Enabled push notifications',
            },
          ],
        },
        {
          eventType: 'PURCHASED_REWARD',
          points: 0,
          reactors: [
            {
              points: 0,
              reactorName: 'Gift Card Email - Delayed',
            },
            {
              points: 0,
              reactorName: 'Reward Purchase',
            },
          ],
        },
        {
          eventType: 'XOXO_ORDER_DELIVERED',
          points: 0,
          reactors: [
            {
              points: 0,
              reactorName: 'Gift Card Email - Delivered',
            },
          ],
        },
        {
          eventType: 'IMPROVED_CREDIT_UTILIZATION_BELOW_TEN',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Improved credit utilization - below 10%',
            },
          ],
        },
        {
          eventType: 'IMPROVED_CREDIT_UTILIZATION_BELOW_THIRTY',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Improved credit utilization - below 30%',
            },
          ],
        },
        {
          eventType: 'LINKED_ALL_ACCOUNT_TYPES',
          points: 100,
          reactors: [
            {
              points: 100,
              reactorName: 'Linked all account types',
            },
          ],
        },
        {
          eventType: 'LINKED_AUTO_INSURANCE_POLICY',
          points: 150,
          reactors: [
            {
              points: 150,
              reactorName: 'Linked Auto insurance policy',
            },
          ],
        },
        {
          eventType: 'LINKED_CREDIT_CARD_ACCOUNT',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Linked Credit Card Account',
            },
          ],
        },
        {
          eventType: 'LINKED_HOME_OR_RENTERS_INSURANCE_POLICY',
          points: 150,
          reactors: [
            {
              points: 150,
              reactorName: 'Linked Home or Renters insurance policy',
            },
          ],
        },
        {
          eventType: 'LINKED_INVESTMENT_ACCOUNT',
          points: 100,
          reactors: [
            {
              points: 100,
              reactorName: 'Linked Investment Account',
            },
          ],
        },
        {
          eventType: 'LINKED_LOAN_ACCOUNT',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Linked Loan Account',
            },
          ],
        },
        {
          eventType: 'LINKED_SAVINGS_ACCOUNT',
          points: 200,
          reactors: [
            {
              points: 200,
              reactorName: 'Linked Savings Account',
            },
          ],
        },
        {
          eventType: 'LOGGED_INTO_NERDWALLET_APP',
          points: 300,
          reactors: [
            {
              points: 300,
              reactorName: 'Logged into NerdWallet App',
            },
          ],
        },
        {
          eventType: 'MADE_DIRECT_DEPOSIT_FOR_ELIGIBLE_BANK_ACCOUNT',
          points: 10000,
          reactors: [
            {
              points: 10000,
              reactorName: 'Made direct deposit',
            },
          ],
        },
        {
          eventType: 'PAID_ELIGIBLE_CREDIT_CARD_ON_TIME',
          points: 10000,
          reactors: [
            {
              points: 10000,
              reactorName: 'On-time payment made for eligible credit card',
            },
          ],
        },
        {
          eventType: 'PAID_MONTHLY_MORTGAGE_ON_TIME',
          points: 10000,
          reactors: [
            {
              points: 10000,
              reactorName: 'On-time payment made for mortgage',
            },
          ],
        },
        {
          eventType: 'PAID_CREDIT_CARD_ON_TIME_MONTHLY',
          points: 10000,
          reactors: [
            {
              points: 10000,
              reactorName: 'Pay your monthly credit card bill by its due date',
            },
          ],
        },
        {
          eventType: 'POINTS_ADJUSTED',
          points: 0,
          reactors: [
            {
              points: 0,
              reactorName: 'Points Adjusted',
            },
          ],
        },
        {
          eventType: 'QA_TEST_EVENT',
          points: 35000,
          reactors: [
            {
              points: 35000,
              reactorName: 'QA Test Reactor',
            },
          ],
        },
        {
          eventType: 'QA_TEST_EVENT_TWENTY_FIVE_THOUSAND',
          points: 25000,
          reactors: [
            {
              points: 25000,
              reactorName: 'QA Test Reactor 25K',
            },
          ],
        },
        {
          eventType: 'REFERRED_A_USER',
          points: 500,
          reactors: [
            {
              points: 500,
              reactorName: 'Referred a NW+ user',
            },
          ],
        },
        {
          eventType: 'SIGNED_UP_VIA_REFERRAL',
          points: 500,
          reactors: [
            {
              points: 500,
              reactorName: 'Registered for NW+ via referral',
            },
          ],
        },
        {
          eventType: 'SAVINGS_ACCOUNT_FUNDED',
          points: 10000,
          reactors: [
            {
              points: 10000,
              reactorName: 'Savings account funded',
            },
          ],
        },
        {
          eventType: 'SET_FINANCIAL_PRIORITIES',
          points: 100,
          reactors: [
            {
              points: 100,
              reactorName: 'Set Financial Priorities',
            },
          ],
        },
        {
          eventType: 'TESTED_PUBLIC_POSTMAN_EVENT',
          points: 5000,
          reactors: [
            {
              points: 5000,
              reactorName: 'Tested Reaction',
            },
          ],
        },
        {
          eventType: 'USED_CREDIT_CARD_FINDER',
          points: 300,
          reactors: [
            {
              points: 300,
              reactorName: 'Used credit card finder',
            },
          ],
        },
        {
          eventType: 'USED_CREDIT_SIMULATOR',
          points: 100,
          reactors: [
            {
              points: 100,
              reactorName: 'Used credit simulator',
            },
          ],
        },
        {
          eventType: 'USED_EMERGENCY_FUND_CALCULATOR',
          points: 150,
          reactors: [
            {
              points: 150,
              reactorName: 'Used emergency fund calculator',
            },
          ],
        },
        {
          eventType: 'USED_RETIREMENT_CALCULATOR',
          points: 150,
          reactors: [
            {
              points: 150,
              reactorName: 'Used retirement calculator',
            },
          ],
        },
        {
          eventType: 'VIEWED_FIFTY_THIRTY_TWENTY_BUDGET',
          points: 25,
          reactors: [
            {
              points: 25,
              reactorName: 'Viewed 50/30/20 Budget',
            },
          ],
        },
        {
          eventType: 'VIEWED_CREDIT_SCORE',
          points: 100,
          reactors: [
            {
              points: 100,
              reactorName: 'Viewed Credit Score in first month',
            },
            {
              points: 100,
              reactorName: 'Viewed Credit Score in second month',
            },
            {
              points: 100,
              reactorName: 'Viewed Credit Score in third month',
            },
          ],
        },
        {
          eventType: 'VIEWED_WEEKLY_READS',
          points: 10,
          reactors: [
            {
              points: 10,
              reactorName: 'Viewed weekly reads',
            },
          ],
        },
        {
          eventType: 'FOUND_RETIREMENT_ACCOUNT_WITH_CAPITALIZE',
          points: 10,
          reactors: [
            {
              points: 10,
              reactorName: 'Capitalize rollover',
            },
            {
              points: 10,
              reactorName: 'Found retirement account with capitalize',
            },
          ],
        },
        {
          eventType: 'ROLLED_OVER_RETIREMENT_ACCOUNT_WITH_CAPITALIZE',
          points: 20,
          reactors: [
            {
              points: 20,
              reactorName: 'Rolled over retirement account with capitalize',
            },
          ],
        },
        {
          eventType: 'TREASURY_ACCOUNT_FUNDS_HELD',
          points: 20,
          reactors: [
            {
              points: 20,
              reactorName:
                'Fund your treasury account with $5,000 2,000 pts ($20.00)',
            },
          ],
        },
      ],
    },
  },
};

export default useRewardsPoints;
